
export interface ScoreDimension { rawScore: number; maxScore: number; weight: number; weightedScore: number; description: string; }
export interface IdeaResult { overallScore: number; maxScore: number; verdict: string; dimensions: Record<string, ScoreDimension>; strengths: string[]; weaknesses: string[]; suggestions: string[]; }

const VIABILITY_RUBRIC: Record<string, { weight: number; criteria: Record<string, { score: number; desc: string }> }> = {
  problem_severity: {
    weight: 25,
    criteria: {
      critical_pain: { score: 10, desc: 'Solves a critical pain people pay to fix immediately' },
      significant_pain: { score: 7, desc: 'Solves a significant pain people complain about regularly' },
      moderate_pain: { score: 5, desc: 'Solves a moderate inconvenience people tolerate' },
      minor_pain: { score: 2, desc: 'Nice-to-have, not urgent' },
      no_pain: { score: 0, desc: 'No real problem identified' }
    }
  },
  market_size: {
    weight: 20,
    criteria: {
      mass_market: { score: 10, desc: 'Millions of potential users' },
      large_niche: { score: 7, desc: 'Hundreds of thousands in niche' },
      small_niche: { score: 5, desc: 'Tens of thousands in niche' },
      micro_niche: { score: 3, desc: 'Thousands in niche' },
      unknown: { score: 0, desc: 'Cannot estimate market size' }
    }
  },
  competition_density: {
    weight: 20,
    criteria: {
      blue_ocean: { score: 10, desc: 'No direct competitors, first mover' },
      few_competitors: { score: 7, desc: '2-3 competitors, room to differentiate' },
      crowded: { score: 4, desc: 'Many competitors, hard to stand out' },
      dominated: { score: 1, desc: 'Market dominated by 1-2 giants' },
      unknown: { score: 0, desc: 'Competition not researched' }
    }
  },
  build_simplicity: {
    weight: 20,
    criteria: {
      weekend_project: { score: 10, desc: 'Can build MVP in 1-2 weekends' },
      month_project: { score: 7, desc: 'Can build MVP in 2-4 weeks' },
      quarter_project: { score: 5, desc: 'Needs 1-3 months for MVP' },
      complex_product: { score: 2, desc: 'Needs 3-6 months for MVP' },
      enterprise_grade: { score: 0, desc: 'Needs 6+ months, team required' }
    }
  },
  monetization_clarity: {
    weight: 15,
    criteria: {
      clear_one_time: { score: 10, desc: 'Clear one-time purchase model' },
      clear_subscription: { score: 8, desc: 'Clear recurring revenue model' },
      freemium_viable: { score: 6, desc: 'Freemium with clear upgrade path' },
      unclear: { score: 2, desc: 'Monetization path not obvious' },
      no_monetization: { score: 0, desc: 'No clear way to charge' }
    }
  }
};

export function scoreIdea(answers: Record<string, string>): IdeaResult {
  let total = 0;
  let maxPossible = 0;
  const dimensions: Record<string, ScoreDimension> = {};

  Object.entries(VIABILITY_RUBRIC).forEach(([dim, data]) => {
    const answer = answers[dim] || 'unknown';
    const criterion = data.criteria[answer] || data.criteria.unknown;
    const weighted = (criterion.score / 10) * data.weight;
    dimensions[dim] = { rawScore: criterion.score, maxScore: 10, weight: data.weight, weightedScore: weighted, description: criterion.desc };
    total += weighted;
    maxPossible += data.weight;
  });

  const score = (total / maxPossible) * 10;
  const verdict = score >= 8 ? 'STRONG BUILD' : score >= 6 ? 'PROMISING' : score >= 4 ? 'RISKY' : 'RECONSIDER';

  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const suggestions: string[] = [];

  Object.entries(dimensions).forEach(([dim, data]) => {
    if (data.rawScore >= 7) strengths.push(`${dim.replace('_', ' ')}: ${data.description}`);
    if (data.rawScore <= 3) weaknesses.push(`${dim.replace('_', ' ')}: ${data.description}`);
  });

  if (score < 6) { suggestions.push('Consider narrowing to a more specific pain point'); suggestions.push('Research 3 competitors to validate market exists'); }
  if (dimensions.build_simplicity?.rawScore < 5) suggestions.push('Can you reduce MVP scope to 2-week build?');
  if (dimensions.monetization_clarity?.rawScore < 5) suggestions.push('Define exact price point and who pays before building');

  return { overallScore: +score.toFixed(1), maxScore: 10, verdict, dimensions, strengths, weaknesses, suggestions };
}

export function estimateBuildComplexity(ideaText: string, skills: string[]) {
  let complexity = 5;
  const t = ideaText.toLowerCase();
  ['calculator', 'converter', 'tracker', 'checklist', 'template', 'form', 'quiz'].forEach(kw => { if (t.includes(kw)) complexity -= 1; });
  ['dashboard', 'api', 'integration', 'auth', 'database', 'export', 'import'].forEach(kw => { if (t.includes(kw)) complexity += 1; });
  ['real-time', 'machine learning', 'blockchain', 'video', 'audio', 'collaborative', 'multi-tenant'].forEach(kw => { if (t.includes(kw)) complexity += 3; });
  complexity = Math.max(1, Math.min(10, complexity));

  const hours = complexity * 8;
  const weeks = Math.ceil(hours / 20);

  const skillKeywords: Record<string, string[]> = {
    frontend: ['ui', 'interface', 'design', 'responsive', 'css', 'react', 'vue'],
    backend: ['api', 'database', 'server', 'auth', 'payment', 'integration'],
    ai: ['ai', 'ml', 'gpt', 'openai', 'llm', 'automation', 'smart'],
    devops: ['deploy', 'host', 'serverless', 'ci/cd', 'docker'],
    marketing: ['seo', 'landing page', 'funnel', 'email', 'analytics']
  };

  let match = 0, total = 0;
  const normalizedSkills = skills.map(s => {
    const lower = s.toLowerCase().trim();
    return lower === 'ai/ml' ? 'ai' : lower;
  });
  Object.entries(skillKeywords).forEach(([skill, keywords]) => {
    if (normalizedSkills.includes(skill)) { total++; if (keywords.some(kw => t.includes(kw))) match++; }
  });
  const skillMatch = total > 0 ? Math.round((match / total) * 100) : 50;

  let buildPrompt = `Build a focused PWA that ${ideaText.toLowerCase()}.`;
  if (complexity <= 3) buildPrompt += ' Use vanilla JS + Tailwind. Single page. LocalStorage. No backend needed.';
  else if (complexity <= 6) buildPrompt += ' Use React/Vue components. Simple Supabase backend for persistence. PWA installable.';
  else buildPrompt += ' Use full-stack framework. Database + API + Auth. Consider starting with simpler MVP.';

  return { complexityScore: complexity, estimatedHours: hours, estimatedWeeks: weeks, skillMatch, buildPrompt };
}

export function generateCompetitorAnalysis(category: string) {
  const categories: Record<string, string[]> = {
    productivity: ['Notion', 'Trello', 'Asana', 'ClickUp', 'Monday'],
    ai_tools: ['ChatGPT', 'Claude', 'Jasper', 'Copy.ai', 'Grammarly'],
    finance: ['QuickBooks', 'FreshBooks', 'Wave', 'Xero'],
    marketing: ['HubSpot', 'Mailchimp', 'ConvertKit', 'ActiveCampaign'],
    development: ['GitHub', 'GitLab', 'Vercel', 'Railway']
  };
  return {
    direct: categories[category] || ['Research needed'],
    angles: [
      'Focus on a narrower niche within this space',
      'One-time payment vs subscription fatigue',
      'Offline-first / privacy-focused angle',
      'Specific integration (e.g., works with Notion only)',
      'Simpler UX — remove 80% of features competitors bloat'
    ],
    tip: 'Look for 1-star reviews on competitor pages — those complaints are your feature list'
  };
}
